import React from 'react'
import NextHead from 'next/head'

import { buildBaseURL, buildCanonicalURL } from 'lib/utils/page'
import HrefLangs from 'src/comps/HrefLangs'
import { generateRichHeadHotel } from 'lib/utils/richHead'
import type { PropertyAPIResponse } from 'src/types/property'
import { useMarket } from 'src/hooks/useMarket'
import { renderSrc } from 'src/helpers/imageHandler'

interface Input {
  pathname: string
  locale: string
  property: PropertyAPIResponse
}

const Head = ({ pathname, locale, property }: Input) => {
  const title = property?.name ? `${property?.name} - Stayforlong` : ''
  const { generic, amenities, businessAmenities, rooms, location, general } = property.description
  const description =
    general || generic || location || rooms || amenities || businessAmenities || ''
  const { market, language, subdomain, topdomain } = useMarket()
  const canonicalURL = buildCanonicalURL({ pathname, market, language, subdomain, topdomain })
  const baseURL = buildBaseURL({ subdomain, topdomain })
  const ldJSON = generateRichHeadHotel(property, canonicalURL)
  const photoURL = property?.photo ? renderSrc({ imgObj: property.photo, width: 580 }) : ''

  return (
    <>
      <NextHead>
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalURL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Stayforlong" />
        <meta property="og:image" content={photoURL} />
        <meta property="og:locale" content={locale.replace('-', '_')} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:domain" content={baseURL} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={photoURL} />
        <meta name="twitter:url" content={canonicalURL} />
        <script
          id="hotel"
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJSON) }}
        />
        <link rel="canonical" href={canonicalURL} />
      </NextHead>
      <HrefLangs pathname={pathname} />
    </>
  )
}

export default Head
